import React, {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { apiFetch } from "../api/api";

const FILTER_DRAWER_WIDTH = 340;
const DESKTOP_FILTER_LEFT = 48; // matches PublicLayout md px={6}
const DESKTOP_FILTER_TOP = 208; // align with filter/results row so hero stays visible
const HOLDER_PIE_COLORS = [
  "#8B5CF6",
  "#38BDF8",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#A78BFA",
  "#14B8A6",
  "#FB7185",
  "#84CC16",
  "#F97316",
];
const MARKET_CAP_PIE_COLORS = ["#2563EB", "#F59E0B", "#22C55E"];
const TRACKING_ERROR_RANGES = [
  { value: "minus015-plus015", label: "-0.15 to +0.15", min: -0.15, max: 0.15 },
  { value: "plus015-plus045", label: "+0.15 to +0.45", min: 0.15, max: 0.45 },
  { value: "plus045-plus075", label: "+0.45 to +0.75", min: 0.45, max: 0.75 },
  { value: "plus075-plus150", label: "+0.75 to +1.50", min: 0.75, max: 1.5 },
  { value: "gt-plus150", label: "> +1.50", min: 1.5, max: null, exclusiveMin: true },
];

const defaultFilters = {
  q: "",
  assetid: "",
  subclassid: "",
  sub_subclassid: "",
  amcid: "",
  trackingErrorRange: "",
};

const fmtNum = (v, digits = 2) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const fmtExpenseRatio = (v) => fmtNum(v, 4);

const fmtSignedPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
};

const fmt2 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return n.toFixed(2);
};

const getPremiumInfo = (ltpValue, navValue) => {
  const ltp = Number(ltpValue);
  const nav = Number(navValue);
  if (!Number.isFinite(ltp) || !Number.isFinite(nav) || nav === 0) return null;

  const premiumPct = ((ltp - nav) / nav) * 100;
  const absPct = Math.abs(premiumPct);

  let label = "HIGH";
  let tone = "error";

  if (premiumPct >= -0.5 && premiumPct <= 0.5) {
    label = "Fair Value";
    tone = "success";
  } else if (absPct <= 1.5) {
    label = "Mild";
    tone = "info";
  } else if (absPct <= 3) {
    label = "Moderate";
    tone = "warning";
  }

  return { premiumPct, label, tone };
};

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toLower = (v) => String(v || "").toLowerCase();

const inTrackingErrorRange = (value, rangeValue) => {
  if (!rangeValue) return true;
  const range = TRACKING_ERROR_RANGES.find((item) => item.value === rangeValue);
  const n = toNum(value);
  if (!range || n === null) return false;
  if (toNum(range.min) !== null) {
    if (range.exclusiveMin ? n <= Number(range.min) : n < Number(range.min)) return false;
  }
  if (toNum(range.max) !== null && n > Number(range.max)) return false;
  return true;
};

const toSafeWebsiteHref = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.toString();
  } catch {
    return "";
  }
};

const getSymbolKey = (row) =>
  String(row?.symbol || "")
    .trim()
    .toUpperCase();

const getLatestDailyForEtf = (etf, dailySnapshotBySymbol) =>
  dailySnapshotBySymbol[getSymbolKey(etf)] || null;

const compareMetricDefs = [
  { label: "ETF Name", type: "text", get: (etf) => etf?.etf_name },
  { label: "Symbol", type: "text", get: (etf) => etf?.symbol },
  { label: "AMC", type: "text", get: (etf) => etf?.amcname },
  { label: "Asset class", type: "text", get: (etf) => etf?.asset_name },
  { label: "Subclass", type: "text", get: (etf) => etf?.subclass_name },
  { label: "Sub-subclass", type: "text", get: (etf) => etf?.subsubclass_name },
  { label: "Benchmark", type: "text", get: (etf) => etf?.biname },
  { label: "Launch date", type: "text", get: (etf) => etf?.launch_date },
  { label: "AUM (Crores)", type: "number", get: (etf) => etf?.aum_crores },
  { label: "Expense ratio", type: "number", digits: 4, get: (etf) => etf?.expense_ratio },
  { label: "Tracking error", type: "number", get: (etf) => etf?.overall_tracking_error },
  {
    label: "Tracking difference",
    type: "number",
    get: (etf) => etf?.overall_tracking_difference,
  },
  { label: "Years active", type: "number", get: (etf) => etf?.years_active },
  { label: "LTP", type: "number", get: (_, daily) => daily?.ltp },
  { label: "NAV", type: "number", get: (_, daily) => daily?.nav },
  {
    label: "Premium / Discount %",
    type: "percent",
    get: (_, daily) => getPremiumInfo(daily?.ltp, daily?.nav)?.premiumPct,
  },
  { label: "Daily change %", type: "percent", get: (_, daily) => daily?.change_percent },
  { label: "Volume", type: "number", digits: 0, get: (_, daily) => daily?.volume },
  { label: "Value crores", type: "number", get: (_, daily) => daily?.value_crores },
  { label: "52W high", type: "number", get: (_, daily) => daily?.week52_high },
  { label: "52W low", type: "number", get: (_, daily) => daily?.week52_low },
  { label: "Latest daily date", type: "text", get: (_, daily) => daily?.trade_date },
];

const formatCompareValue = (value, metric) => {
  if (metric.type === "percent") return fmtSignedPct(value);
  if (metric.type === "number") return fmtNum(value, metric.digits ?? 2);
  return value || "-";
};

const buildCategoryTree = (assetClasses, subClasses, subSubClasses) =>
  (assetClasses || []).map((asset) => {
    const assetChildren = (subClasses || [])
      .filter((sub) => String(sub.assetid) === String(asset.assetid))
      .map((sub) => {
        const subChildren = (subSubClasses || [])
          .filter(
            (subSub) =>
              String(subSub.assetid) === String(asset.assetid) &&
              String(subSub.assetsubid) === String(sub.assetsubid)
          )
          .map((subSub) => ({
            itemId: `subsub-${subSub.subsubclassid}`,
            label: subSub.subsubclass_name,
            isLeaf: true,
            assetid: String(asset.assetid),
            subclassid: String(sub.assetsubid),
            sub_subclassid: String(subSub.subsubclassid),
            children: [],
          }));

        const hasSubChildren = Boolean(sub.has_subclass) && subChildren.length > 0;

        return {
          itemId: `sub-${sub.assetsubid}`,
          label: sub.subclass_name,
          isLeaf: !hasSubChildren,
          assetid: String(asset.assetid),
          subclassid: String(sub.assetsubid),
          sub_subclassid: "",
          children: hasSubChildren ? subChildren : [],
        };
      });

    return {
      itemId: `asset-${asset.assetid}`,
      label: asset.asset_name,
      isLeaf: assetChildren.length === 0,
      assetid: String(asset.assetid),
      subclassid: "",
      sub_subclassid: "",
      children: assetChildren,
    };
  });

const buildTreeLookup = (nodes) => {
  const lookup = {};
  const walk = (items) => {
    items.forEach((item) => {
      lookup[item.itemId] = item;
      if (item.children?.length) walk(item.children);
    });
  };
  walk(nodes);
  return lookup;
};

const renderTreeItems = (nodes) =>
  nodes.map((node) => (
    <TreeItem
      key={node.itemId}
      itemId={node.itemId}
      label={
        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ py: 0.15 }}>
          <Typography variant="body2" sx={{ fontWeight: node.isLeaf ? 700 : 600 }}>
            {node.label}
          </Typography>
        </Stack>
      }
      sx={{
        "& .MuiTreeItem-content": {
          borderRadius: 1.5,
          py: 0.3,
        },
        "& .MuiTreeItem-content.Mui-selected": {
          backgroundColor: "rgba(37, 99, 235, 0.12)",
        },
        "& .MuiTreeItem-content.Mui-selected:hover": {
          backgroundColor: "rgba(37, 99, 235, 0.16)",
        },
      }}
    >
      {node.children?.length ? renderTreeItems(node.children) : null}
    </TreeItem>
  ));

function Metric({ label, value }) {
  const displayValue = value === null || value === undefined || value === "" ? "-" : value;
  return (
    <Stack spacing={0.2}>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {displayValue}
      </Typography>
    </Stack>
  );
}

export default function EtfExplorer({ title, subtitle, cta }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [dropdowns, setDropdowns] = useState({
    asset_class: [],
    asset_sub_class: [],
    sub_subclass: [],
    amc: [],
  });
  const [searchText, setSearchText] = useState(defaultFilters.q);
  const [filters, setFilters] = useState(defaultFilters);
  const [etfs, setEtfs] = useState([]);
  const [allEtfs, setAllEtfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dailySnapshotBySymbol, setDailySnapshotBySymbol] = useState({});
  const [dailyEffectiveDate, setDailyEffectiveDate] = useState("");
  const [expandedItems, setExpandedItems] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1200;
  });

  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [holdersView, setHoldersView] = useState("list");
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [detailData, setDetailData] = useState({
    etf: null,
    top_holders: [],
    holders_snapshot_date: null,
    market_cap_segments: [],
    market_cap_snapshot_date: null,
  });
  const deferredSearchText = useDeferredValue(searchText);

  useEffect(() => {
    setFiltersOpen(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    setFilters((prev) => {
      if (prev.q === deferredSearchText) return prev;
      return { ...prev, q: deferredSearchText };
    });
  }, [deferredSearchText]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const data = await apiFetch("/api/v1/auth/etf/master-drpdowns");
        setDropdowns({
          asset_class: data.asset_class || [],
          asset_sub_class: data.asset_sub_class || [],
          sub_subclass: data.sub_subclass || [],
          amc: data.amc || [],
        });
      } catch (_) {
        // keep UI usable if metadata fetch fails
      }
    };

    loadDropdowns();
  }, []);

  useEffect(() => {
    const loadAllEtfs = async () => {
      try {
        const data = await apiFetch("/api/v1/auth/etf/master/etf_master");
        setAllEtfs(data.rows || []);
      } catch (_) {
        setAllEtfs([]);
      }
    };

    loadAllEtfs();
  }, []);

  useEffect(() => {
    const loadEtfs = async () => {
      setLoading(true);
      setError("");
      try {
        const qs = new URLSearchParams();
        if (filters.assetid) qs.set("assetid", filters.assetid);
        if (filters.subclassid) qs.set("subclassid", filters.subclassid);
        if (filters.sub_subclassid) qs.set("sub_subclassid", filters.sub_subclassid);
        if (filters.amcid) qs.set("amcid", filters.amcid);

        const url = `/api/v1/auth/etf/master/etf_master${
          qs.toString() ? `?${qs.toString()}` : ""
        }`;
        const data = await apiFetch(url);
        setEtfs(data.rows || []);
        if (!qs.toString()) setAllEtfs(data.rows || []);
      } catch (err) {
        setError(err.message || "Failed to load ETFs");
        setEtfs([]);
      } finally {
        setLoading(false);
      }
    };

    loadEtfs();
  }, [filters.assetid, filters.subclassid, filters.sub_subclassid, filters.amcid]);

  useEffect(() => {
    const loadDailySnapshot = async () => {
      try {
        const data = await apiFetch("/api/v1/auth/etf/daily/symbols?limit=2000");
        const nextBySymbol = {};
        (data.rows || []).forEach((row) => {
          const key = String(row.symbol || "")
            .trim()
            .toUpperCase();
          if (key) nextBySymbol[key] = row;
        });
        setDailySnapshotBySymbol(nextBySymbol);
        setDailyEffectiveDate(data.effective_date || "");
      } catch (_) {
        setDailySnapshotBySymbol({});
        setDailyEffectiveDate("");
      }
    };

    loadDailySnapshot();
  }, []);

  const categoryTree = useMemo(
    () =>
      buildCategoryTree(
        dropdowns.asset_class,
        dropdowns.asset_sub_class,
        dropdowns.sub_subclass
      ),
    [dropdowns.asset_class, dropdowns.asset_sub_class, dropdowns.sub_subclass]
  );

  const treeLookup = useMemo(() => buildTreeLookup(categoryTree), [categoryTree]);

  const selectedTreeItem = useMemo(() => {
    if (filters.sub_subclassid) return `subsub-${filters.sub_subclassid}`;
    if (filters.subclassid) return `sub-${filters.subclassid}`;
    if (filters.assetid && treeLookup[`asset-${filters.assetid}`]?.isLeaf) {
      return `asset-${filters.assetid}`;
    }
    return null;
  }, [filters.assetid, filters.subclassid, filters.sub_subclassid, treeLookup]);

  const selectedAmc = useMemo(
    () =>
      (dropdowns.amc || []).find((row) => String(row.amcid) === String(filters.amcid)) || null,
    [dropdowns.amc, filters.amcid]
  );

  const selectedCategoryLabel = useMemo(() => {
    const parts = [];
    const asset = (dropdowns.asset_class || []).find(
      (row) => String(row.assetid) === String(filters.assetid)
    );
    const sub = (dropdowns.asset_sub_class || []).find(
      (row) => String(row.assetsubid) === String(filters.subclassid)
    );
    const subSub = (dropdowns.sub_subclass || []).find(
      (row) => String(row.subsubclassid) === String(filters.sub_subclassid)
    );

    if (asset?.asset_name) parts.push(asset.asset_name);
    if (sub?.subclass_name) parts.push(sub.subclass_name);
    if (subSub?.subsubclass_name) parts.push(subSub.subsubclass_name);

    return parts.join(" / ");
  }, [
    dropdowns.asset_class,
    dropdowns.asset_sub_class,
    dropdowns.sub_subclass,
    filters.assetid,
    filters.subclassid,
    filters.sub_subclassid,
  ]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchText.trim()) count += 1;
    if (filters.amcid) count += 1;
    if (filters.trackingErrorRange) count += 1;
    if (selectedCategoryLabel) count += 1;
    return count;
  }, [
    searchText,
    filters.amcid,
    filters.trackingErrorRange,
    selectedCategoryLabel,
  ]);

  const filteredEtfs = useMemo(() => {
    const q = toLower(deferredSearchText).trim();

    return (etfs || []).filter((row) => {
      if (filters.assetid && String(row.assetid) !== String(filters.assetid)) return false;
      if (filters.subclassid && String(row.subclassid) !== String(filters.subclassid)) {
        return false;
      }
      if (
        filters.sub_subclassid &&
        String(row.sub_subclassid || "") !== String(filters.sub_subclassid)
      ) {
        return false;
      }
      if (filters.amcid && String(row.amcid) !== String(filters.amcid)) return false;

      if (q) {
        const hay = [
          row.etf_name,
          row.symbol,
          row.isin_code,
          row.amcname,
          row.biname,
          row.asset_name,
          row.subclass_name,
          row.subsubclass_name,
          row.website_link,
          row.bse_ticker,
        ]
          .map((x) => toLower(x))
          .join(" ");
        if (!hay.includes(q)) return false;
      }

      if (!inTrackingErrorRange(row.overall_tracking_error, filters.trackingErrorRange)) {
        return false;
      }

      return true;
    });
  }, [
    deferredSearchText,
    etfs,
    filters.amcid,
    filters.assetid,
    filters.sub_subclassid,
    filters.subclassid,
    filters.trackingErrorRange,
  ]);

  const selectedBucketSummary = useMemo(() => {
    if (!selectedCategoryLabel) return null;
    const totalAum = filteredEtfs.reduce((acc, row) => {
      const aum = toNum(row.aum_crores);
      return acc + (aum || 0);
    }, 0);
    return {
      count: filteredEtfs.length,
      totalAum,
    };
  }, [filteredEtfs, selectedCategoryLabel]);

  useEffect(() => {
    if (!filters.assetid) return;
    const parents = [`asset-${filters.assetid}`];
    if (filters.subclassid) parents.push(`sub-${filters.subclassid}`);
    setExpandedItems((prev) => Array.from(new Set([...prev, ...parents])));
  }, [filters.assetid, filters.subclassid]);

  const applyCategoryNode = (itemId) => {
    const node = treeLookup[itemId];
    if (!node?.isLeaf) return;

    setSearchText("");
    startTransition(() => {
      setFilters((prev) => ({
        ...prev,
        q: "",
        amcid: "",
        assetid: node.assetid || "",
        subclassid: node.subclassid || "",
        sub_subclassid: node.sub_subclassid || "",
      }));
    });

    setExpandedItems((prev) =>
      Array.from(
        new Set([
          ...prev,
          `asset-${node.assetid}`,
          ...(node.subclassid ? [`sub-${node.subclassid}`] : []),
        ])
      )
    );

    if (!isDesktop) {
      startTransition(() => setFiltersOpen(false));
    }
  };

  const openDetails = useCallback(async (row) => {
    setSelected(row);
    setDetailOpen(true);
    setHoldersView("list");
    setDetailError("");
    setDetailLoading(true);
    setDetailData({
      etf: row,
      top_holders: [],
      holders_snapshot_date: null,
      market_cap_segments: [],
      market_cap_snapshot_date: null,
    });

    try {
      const data = await apiFetch(`/api/v1/auth/etf/master/etf_master/${row.etf_id}/details`);
      setDetailData({
        etf: data?.etf || row,
        top_holders: data?.top_holders || [],
        holders_snapshot_date: data?.holders_snapshot_date || null,
        market_cap_segments: data?.market_cap_segments || [],
        market_cap_snapshot_date: data?.market_cap_snapshot_date || null,
      });
    } catch (err) {
      setDetailError(err.message || "Unable to load ETF details");
      setDetailData({
        etf: row,
        top_holders: [],
        holders_snapshot_date: null,
        market_cap_segments: [],
        market_cap_snapshot_date: null,
      });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const resetFilters = () => {
    const activeEl = typeof document !== "undefined" ? document.activeElement : null;
    if (activeEl instanceof HTMLElement) activeEl.blur();
    setSearchText(defaultFilters.q);
    setFilters(defaultFilters);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  };

  const clearCategoryFilters = () =>
    setFilters((prev) => ({
      ...prev,
      assetid: "",
      subclassid: "",
      sub_subclassid: "",
    }));

  const renderField = (label, value, { asLink = false } = {}) => {
    const href = asLink ? toSafeWebsiteHref(value) : "";
    return (
      <Stack direction="row" justifyContent="space-between" sx={{ py: 0.7 }}>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {label}
        </Typography>
        {href ? (
          <Typography
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
              fontWeight: 700,
              textAlign: "right",
              ml: 2,
              color: "primary.main",
              textDecoration: "underline",
              wordBreak: "break-all",
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 700, textAlign: "right", ml: 2 }}>
            {value === null || value === undefined || value === "" ? "-" : value}
          </Typography>
        )}
      </Stack>
    );
  };

  const detailEtf = detailData.etf || selected;
  const compareOptions = allEtfs.length ? allEtfs : etfs;
  const compareReady =
    Boolean(compareA?.etf_id) &&
    Boolean(compareB?.etf_id) &&
    String(compareA.etf_id) !== String(compareB.etf_id);
  const compareBenchmarkInfo = useMemo(() => {
    if (!compareReady) return null;
    const left = String(compareA?.biname || "").trim();
    const right = String(compareB?.biname || "").trim();
    if (!left || !right) {
      return {
        same: false,
        label: "Benchmark data missing for one ETF",
        color: "text.secondary",
      };
    }
    const same = left.toLowerCase() === right.toLowerCase();
    return {
      same,
      label: same ? "Same benchmark" : "Different benchmark",
      color: same ? "success.main" : "warning.main",
    };
  }, [compareA?.biname, compareB?.biname, compareReady]);
  const compareRows = useMemo(() => {
    if (!compareReady) return [];
    const dailyA = getLatestDailyForEtf(compareA, dailySnapshotBySymbol);
    const dailyB = getLatestDailyForEtf(compareB, dailySnapshotBySymbol);

    return compareMetricDefs.map((metric) => {
      const leftRaw = metric.get(compareA, dailyA);
      const rightRaw = metric.get(compareB, dailyB);
      return {
        label: metric.label,
        left: formatCompareValue(leftRaw, metric),
        right: formatCompareValue(rightRaw, metric),
      };
    });
  }, [compareA, compareB, compareReady, dailySnapshotBySymbol]);
  const hasTopHolders = (detailData.top_holders || []).length > 0;
  const hasMarketCapSegments = (detailData.market_cap_segments || []).length > 0;
  const holdersPieData = useMemo(
    () =>
      (detailData.top_holders || [])
        .map((holder, index) => ({
          id: `${holder.name || "holder"}-${index}`,
          name: holder.name || `Holder ${index + 1}`,
          value: Number(holder.weight),
        }))
        .filter((item) => Number.isFinite(item.value) && item.value > 0),
    [detailData.top_holders]
  );
  const marketCapPieData = useMemo(
    () =>
      (detailData.market_cap_segments || [])
        .map((segment, index) => ({
          id: `${segment.name || "segment"}-${index}`,
          name: segment.name || `Segment ${index + 1}`,
          value: Number(segment.weight),
        }))
        .filter((item) => Number.isFinite(item.value) && item.value > 0),
    [detailData.market_cap_segments]
  );
  const renderedEtfCards = useMemo(
    () =>
      filteredEtfs.map((row) => (
        <Paper key={row.etf_id} elevation={0} sx={{ p: 2, borderRadius: 2.5 }}>
          <Stack spacing={1.2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {row.etf_name}
              </Typography>
              <Chip label={row.symbol || "N/A"} size="small" />
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              {row.amcname || "-"} | {row.biname || "-"}
            </Typography>
            <Divider />
            {(() => {
              const daily = dailySnapshotBySymbol[
                String(row.symbol || "")
                  .trim()
                  .toUpperCase()
              ];
              const premiumInfo = getPremiumInfo(daily?.ltp, daily?.nav);

              return (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 1,
                    p: 1.2,
                    borderRadius: 1.8,
                    background: "rgba(148,163,184,0.08)",
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  <Stack spacing={0.2}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      LTP {dailyEffectiveDate ? `(${dailyEffectiveDate})` : ""}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>
                      {fmtNum(daily?.ltp)}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.2}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      NAV
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>
                      {fmtNum(daily?.nav)}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.2}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Premium / Discount
                    </Typography>
                    {premiumInfo ? (
                      <Stack direction="row" spacing={0.7} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="body1" sx={{ fontWeight: 800 }}>
                          {fmtSignedPct(premiumInfo.premiumPct)}
                        </Typography>
                        <Chip size="small" color={premiumInfo.tone} label={premiumInfo.label} />
                      </Stack>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 800 }}>
                        -
                      </Typography>
                    )}
                  </Stack>
                </Box>
              );
            })()}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.2,
              }}
            >
              <Metric label="Asset class" value={row.asset_name} />
              <Metric label="Subclass" value={row.subclass_name} />
              <Metric label="Tracking error" value={row.overall_tracking_error} />
              <Metric label="AUM (Crores)" value={row.aum_crores} />
              <Metric label="Expense ratio" value={fmtExpenseRatio(row.expense_ratio)} />
              <Metric label="Launch date" value={row.launch_date} />
            </Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ opacity: 0.72 }}>
                Click to view top holders and full ETF details
              </Typography>
              <Button size="small" variant="contained" onClick={() => openDetails(row)}>
                View details
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )),
    [dailyEffectiveDate, dailySnapshotBySymbol, filteredEtfs, openDetails]
  );

  const toggleFilters = () => {
    startTransition(() => setFiltersOpen((prev) => !prev));
  };

  const renderFilterContent = ({ inDrawer = false } = {}) => {
    const enableBodyScroll = inDrawer;
    return (
    <Stack
      spacing={2}
      sx={{
        width: FILTER_DRAWER_WIDTH,
        maxWidth: "100vw",
        p: 2.25,
        ...(enableBodyScroll
          ? {
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }
          : {}),
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Filters
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.35 }}>
              Narrow the ETF list with a smaller, faster filter set.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={toggleFilters}
            startIcon={<CloseRoundedIcon />}
            sx={{ minWidth: 0 }}
          >
            Hide
          </Button>
        </Stack>

        <Button
          variant="outlined"
          startIcon={<RestartAltRoundedIcon />}
          onClick={resetFilters}
        >
          Reset filters
        </Button>
      </Stack>

      <Stack
        spacing={2}
        sx={
          enableBodyScroll
            ? {
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                pr: 0.5,
              }
            : undefined
        }
      >
        <TextField
          size="small"
          label="Search text"
          placeholder="ETF, symbol, AMC, benchmark..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Autocomplete
          size="small"
          options={dropdowns.amc || []}
          value={selectedAmc}
          getOptionLabel={(option) => option?.amcname || ""}
          isOptionEqualToValue={(option, value) => option?.amcid === value?.amcid}
          onChange={(_, value) =>
            startTransition(() =>
              setFilters((prev) => ({
                ...prev,
                amcid: value?.amcid ? String(value.amcid) : "",
              }))
            )
          }
          renderInput={(params) => <TextField {...params} label="AMC" />}
        />

        <Paper
          variant="outlined"
          sx={{
            p: 1.2,
            borderRadius: 2,
            background: "rgba(148,163,184,0.06)",
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Tracking error range
            </Typography>
            {TRACKING_ERROR_RANGES.map((range) => (
              <FormControlLabel
                key={range.value}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.trackingErrorRange === range.value}
                    onChange={(e) =>
                      startTransition(() =>
                        setFilters((prev) => ({
                          ...prev,
                          trackingErrorRange: e.target.checked ? range.value : "",
                        }))
                      )
                    }
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {range.label}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            ))}
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 1.4,
            borderRadius: 2.5,
            background:
              theme.palette.mode === "dark"
                ? "rgba(148,163,184,0.12)"
                : "rgba(15,23,42,0.04)",
          }}
        >
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Asset tree
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.72 }}>
                  Open the branches and click the last node to load that ETF bucket.
                </Typography>
              </Box>
              {selectedCategoryLabel ? (
                <Button size="small" onClick={clearCategoryFilters}>
                  Clear
                </Button>
              ) : null}
            </Stack>

            {categoryTree.length ? (
              <Box
                sx={{
                  maxHeight: "none",
                  overflowY: "visible",
                }}
              >
                <SimpleTreeView
                  expansionTrigger="content"
                  expandedItems={expandedItems}
                  selectedItems={selectedTreeItem}
                  onExpandedItemsChange={(_, itemIds) => setExpandedItems(itemIds)}
                  onItemClick={(_, itemId) => applyCategoryNode(itemId)}
                  slots={{
                    expandIcon: ChevronRightRoundedIcon,
                    collapseIcon: ExpandMoreRoundedIcon,
                  }}
                  sx={{
                    minHeight: 180,
                    "& .MuiTreeItem-groupTransition": {
                      ml: 1,
                      pl: 1.2,
                      borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
                    },
                  }}
                >
                  {renderTreeItems(categoryTree)}
                </SimpleTreeView>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                Category metadata is still loading.
              </Typography>
            )}
          </Stack>
        </Paper>

        {selectedCategoryLabel ? (
          <Chip
            label={selectedCategoryLabel}
            sx={{
              alignSelf: "flex-start",
              maxWidth: "100%",
              "& .MuiChip-label": {
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.68 }}>
            No tree node selected. All ETF categories are currently shown.
          </Typography>
        )}
      </Stack>
    </Stack>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {title || "ETF Details"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.74, mt: 0.2 }}>
              {subtitle ||
                "Browse ETFs with a lighter filter rail, then open each fund for full details."}
            </Typography>
          </Box>
          {cta ? cta : null}
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          alignItems: "flex-start",
        }}
      >
        {isDesktop && filtersOpen ? (
          <Box
            sx={{
              width: FILTER_DRAWER_WIDTH,
              flex: "0 0 auto",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                position: "fixed",
                top: DESKTOP_FILTER_TOP,
                left: DESKTOP_FILTER_LEFT,
                width: FILTER_DRAWER_WIDTH,
                borderRadius: 3,
                height: `calc(100vh - ${DESKTOP_FILTER_TOP + 16}px)`,
                overflowY: "auto",
                overflowX: "hidden",
                zIndex: (t) => t.zIndex.appBar - 1,
              }}
            >
              {renderFilterContent()}
            </Paper>
          </Box>
        ) : null}

        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                p: 2.2,
                borderRadius: 3,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack spacing={0.9}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {!isDesktop || !filtersOpen ? (
                      <Button
                        variant="outlined"
                        startIcon={<TuneRoundedIcon />}
                        onClick={toggleFilters}
                      >
                        {isDesktop ? "Show filters" : "Open filters"}
                      </Button>
                    ) : null}
                    <Chip label={`${filteredEtfs.length} ETFs`} />
                    {activeFilterCount ? <Chip label={`${activeFilterCount} active`} /> : null}
                    {selectedBucketSummary ? (
                      <Chip label={`Bucket: ${selectedBucketSummary.count} ETFs`} color="primary" />
                    ) : null}
                    {selectedBucketSummary ? (
                      <Chip
                        label={`Total AUM: ₹${fmtNum(selectedBucketSummary.totalAum, 2)} Cr`}
                        color="success"
                      />
                    ) : null}
                    <Button
                      variant="outlined"
                      startIcon={<CompareArrowsRoundedIcon />}
                      onClick={() => setCompareOpen(true)}
                    >
                      Compare ETFs
                    </Button>
                  </Stack>

                  <Typography variant="body2" sx={{ opacity: 0.72 }}>
                    {selectedCategoryLabel
                      ? `Selected bucket: ${selectedCategoryLabel}`
                      : "Selected bucket: All ETF categories"}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {error ? <Alert severity="error">{error}</Alert> : null}

            {loading ? (
              <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <CircularProgress size={22} />
                  <Typography variant="body2">Loading ETFs...</Typography>
                </Stack>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                }}
              >
                {renderedEtfCards}
              </Box>
            )}

            {!loading && filteredEtfs.length === 0 ? (
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  No ETFs matched the selected filters.
                </Typography>
              </Paper>
            ) : null}
          </Stack>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={!isDesktop && filtersOpen}
        onClose={() => setFiltersOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: FILTER_DRAWER_WIDTH,
            maxWidth: "100vw",
            height: "100dvh",
          },
        }}
      >
        {renderFilterContent({ inDrawer: true })}
      </Drawer>

      <Dialog open={compareOpen} onClose={() => setCompareOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 800 }}>Compare ETFs</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gap: 1.2,
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              }}
            >
              <Autocomplete
                size="small"
                options={compareOptions}
                value={compareA}
                getOptionLabel={(option) =>
                  option?.etf_name ? `${option.etf_name} (${option.symbol || "N/A"})` : ""
                }
                isOptionEqualToValue={(option, value) =>
                  String(option?.etf_id) === String(value?.etf_id)
                }
                onChange={(_, value) => setCompareA(value)}
                renderInput={(params) => <TextField {...params} label="ETF A" />}
              />
              <Autocomplete
                size="small"
                options={compareOptions}
                value={compareB}
                getOptionLabel={(option) =>
                  option?.etf_name ? `${option.etf_name} (${option.symbol || "N/A"})` : ""
                }
                isOptionEqualToValue={(option, value) =>
                  String(option?.etf_id) === String(value?.etf_id)
                }
                onChange={(_, value) => setCompareB(value)}
                renderInput={(params) => <TextField {...params} label="ETF B" />}
              />
            </Box>

            {!compareReady ? (
              <Alert severity="info">
                Select two different ETFs to compare their core and latest daily data.
              </Alert>
            ) : (
              <Stack spacing={1.4}>
                {compareBenchmarkInfo ? (
                  <Typography
                    variant="subtitle2"
                    sx={{ color: compareBenchmarkInfo.color, fontWeight: 900 }}
                  >
                    {compareBenchmarkInfo.label}
                    {compareA?.biname && compareB?.biname
                      ? `: ${compareA.biname} ${compareBenchmarkInfo.same ? "" : `vs ${compareB.biname}`}`
                      : ""}
                  </Typography>
                ) : null}

                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ minWidth: 640 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1.1fr 1.35fr 1.35fr",
                      gap: 1,
                      px: 1,
                      py: 0.9,
                      borderRadius: 1.5,
                      bgcolor: "rgba(148,163,184,0.14)",
                      fontWeight: 800,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      Metric
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      ETF A
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      ETF B
                    </Typography>
                  </Box>

                  <Stack spacing={0.6} sx={{ mt: 0.8 }}>
                    {compareRows.map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1.1fr 1.35fr 1.35fr",
                          gap: 1,
                          alignItems: "center",
                          px: 1,
                          py: 0.9,
                          borderRadius: 1.5,
                          bgcolor: "rgba(148,163,184,0.08)",
                        }}
                      >
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                          {row.left}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                          {row.right}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
                </Box>
              </Stack>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>
          ETF Details {detailEtf?.etf_name ? `- ${detailEtf.etf_name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {detailError ? <Alert severity="warning">{detailError}</Alert> : null}
          <Stack spacing={2}>
            {detailLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Loading details...</Typography>
              </Stack>
            ) : null}

            <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.8 }}>
                Core information
              </Typography>
              {renderField("ETF Name", detailEtf?.etf_name)}
              {renderField("Symbol", detailEtf?.symbol)}
              {renderField("ISIN", detailEtf?.isin_code)}
              {renderField("AMC", detailEtf?.amcname)}
              {renderField("Benchmark", detailEtf?.biname)}
              {renderField("Asset class", detailEtf?.asset_name)}
              {renderField("Subclass", detailEtf?.subclass_name)}
              {renderField("Sub-subclass", detailEtf?.subsubclass_name)}
              {renderField("Launch date", detailEtf?.launch_date)}
              {renderField("AUM (Crores)", detailEtf?.aum_crores)}
              {renderField("Expense ratio", fmtExpenseRatio(detailEtf?.expense_ratio))}
              {renderField("Tracking error", detailEtf?.overall_tracking_error)}
              {renderField("Tracking difference", detailEtf?.overall_tracking_difference)}
              {renderField("Years active", detailEtf?.years_active)}
              {renderField("Website", detailEtf?.website_link, { asLink: true })}
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Top 10 holders
                </Typography>
                {holdersView === "market-cap" && detailData.market_cap_snapshot_date ? (
                  <Typography variant="caption" sx={{ opacity: 0.72 }}>
                    Snapshot: {detailData.market_cap_snapshot_date}
                  </Typography>
                ) : detailData.holders_snapshot_date ? (
                  <Typography variant="caption" sx={{ opacity: 0.72 }}>
                    Snapshot: {detailData.holders_snapshot_date}
                  </Typography>
                ) : null}
              </Stack>

              {hasTopHolders || hasMarketCapSegments ? (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      variant={holdersView === "list" ? "contained" : "outlined"}
                      onClick={() => setHoldersView("list")}
                    >
                      List view
                    </Button>
                    <Button
                      size="small"
                      variant={holdersView === "pie" ? "contained" : "outlined"}
                      onClick={() => setHoldersView("pie")}
                    >
                      Pie chart
                    </Button>
                    <Button
                      size="small"
                      variant={holdersView === "market-cap" ? "contained" : "outlined"}
                      onClick={() => setHoldersView("market-cap")}
                    >
                      Market cap segmentation
                    </Button>
                  </Stack>

                  {holdersView === "market-cap" ? (
                    marketCapPieData.length ? (
                      <Stack spacing={0.8}>
                        <Box sx={{ height: 280 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={marketCapPieData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={44}
                                outerRadius={98}
                                paddingAngle={1}
                              >
                                {marketCapPieData.map((entry, index) => (
                                  <Cell
                                    key={entry.id}
                                    fill={MARKET_CAP_PIE_COLORS[index % MARKET_CAP_PIE_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <RTooltip formatter={(value) => `${fmt2(value)}%`} />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>

                        <Stack spacing={0.45}>
                          {marketCapPieData.map((entry, index) => (
                            <Stack
                              key={`market-cap-legend-${entry.id}`}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ borderRadius: 1, px: 0.8, py: 0.45, bgcolor: "rgba(148,163,184,0.08)" }}
                            >
                              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: MARKET_CAP_PIE_COLORS[index % MARKET_CAP_PIE_COLORS.length],
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                                  {entry.name}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {fmt2(entry.value)}%
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.72 }}>
                        Market cap segmentation data is not available for this ETF yet.
                      </Typography>
                    )
                  ) : holdersView === "pie" ? (
                    holdersPieData.length ? (
                      <Stack spacing={0.8}>
                        <Box sx={{ height: 280 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={holdersPieData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={44}
                                outerRadius={98}
                                paddingAngle={1}
                              >
                                {holdersPieData.map((entry, index) => (
                                  <Cell
                                    key={entry.id}
                                    fill={HOLDER_PIE_COLORS[index % HOLDER_PIE_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <RTooltip formatter={(value) => `${fmt2(value)}%`} />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>

                        <Stack spacing={0.45}>
                          {holdersPieData.map((entry, index) => (
                            <Stack
                              key={`legend-${entry.id}`}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ borderRadius: 1, px: 0.8, py: 0.45, bgcolor: "rgba(148,163,184,0.08)" }}
                            >
                              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: HOLDER_PIE_COLORS[index % HOLDER_PIE_COLORS.length],
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                                  {entry.name}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {fmt2(entry.value)}%
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.72 }}>
                        Pie chart needs numeric holder weights.
                      </Typography>
                    )
                  ) : (
                    <Stack spacing={0.6}>
                      {hasTopHolders ? (
                        <>
                          <Stack direction="row" sx={{ opacity: 0.72 }}>
                            <Typography variant="caption" sx={{ width: 52 }}>
                              Rank
                            </Typography>
                            <Typography variant="caption" sx={{ flex: 1 }}>
                              Name
                            </Typography>
                            <Typography variant="caption" sx={{ width: 90, textAlign: "right" }}>
                              Weight %
                            </Typography>
                          </Stack>
                          {(detailData.top_holders || []).map((holder, index) => (
                            <Stack
                              key={`${holder.name || "holder"}-${index}`}
                              direction="row"
                              alignItems="center"
                            >
                              <Typography variant="body2" sx={{ width: 52 }}>
                                {holder.rank || index + 1}
                              </Typography>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {holder.name || "-"}
                              </Typography>
                              <Typography variant="body2" sx={{ width: 90, textAlign: "right" }}>
                                {fmt2(holder.weight)}
                              </Typography>
                            </Stack>
                          ))}
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          Top 10 holders data is not available for this ETF yet.
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Top 10 holders data is not available for this ETF yet.
                </Typography>
              )}
            </Paper>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
